import type { ColDef } from "ag-grid-community";
import type { ReactNode } from "react";
import { Checkbox, Tooltip } from "@mui/material";
import {
  numberFormatter,
  percentageFormatter,
  shortDateFormatter,
} from "../../utils/numberFormatter";
import { buildSVGIconSimilarAssetsSVGIcon } from "@/components/common/maps/AssetMapHelperFunctions";
import {
  compValidationTooltipTitle,
  type CompValidationAuditRow,
} from "./compValidationTooltip";

/** Default body column width (px) before halving for denser comps grids. */
const cellW = (_basePx: number) => 100;

function readTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export type CompCheckboxChangeHandler = (
  row: { id?: number },
  checked: boolean,
) => void;

export type CompValidationUserNameResolver = (
  userId: number,
) => string | null;

function centeredAiExtractionCheckbox(
  params: any,
  ariaLabel: string,
  checked: boolean,
  onChange?: CompCheckboxChangeHandler,
  resolveUserName?: CompValidationUserNameResolver,
) {
  if (params.data?.source !== "ai_extraction") {
    return null;
  }

  const tooltip = compValidationTooltipTitle(
    params.data as CompValidationAuditRow,
    resolveUserName,
  );

  const checkbox = (
    <Checkbox
      disableRipple
      size="small"
      checked={checked}
      inputProps={{ "aria-label": ariaLabel }}
      onClick={(event) => {
        event.stopPropagation();
      }}
      onChange={(event) => {
        onChange?.(params.data, event.target.checked);
      }}
      sx={{ color: "#1B7A63 !important", p: 0 }}
    />
  );

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        width: "100%",
      }}
    >
      {tooltip ? (
        <Tooltip title={tooltip} placement="right" arrow>
          <span style={{ display: "inline-flex", lineHeight: 0 }}>{checkbox}</span>
        </Tooltip>
      ) : (
        checkbox
      )}
    </div>
  );
}

function validatedColumn(
  onChange?: CompCheckboxChangeHandler,
  resolveUserName?: CompValidationUserNameResolver,
): ColDef {
  return {
    field: "manually_validated",
    headerName: "Validated",
    width: 72,
    minWidth: 72,
    maxWidth: 88,
    resizable: false,
    cellRenderer: (params: any) =>
      centeredAiExtractionCheckbox(
        params,
        "Validated",
        params.value === true,
        onChange,
        resolveUserName,
      ),
  };
}

function loanLocationWithCountry(params: any): string {
  const country = readTrimmedString(params?.data?.country_name);
  const location = readTrimmedString(params?.data?.location);
  if (country && location) return `${country} (${location})`;
  return country ?? location ?? "";
}

export function dealCompSourceCellRenderer(
  params: any,
  sourceNames: readonly string[],
): ReactNode {
  const source = params.data?.source;
  if (!source) return "";
  const sourceIndex = sourceNames.indexOf(source);
  if (sourceIndex === -1) return "";
  const icon = buildSVGIconSimilarAssetsSVGIcon(sourceIndex + 1);
  const value = params.value;
  if (!value) return "";
  const rawUrl = params.data?.web_url;
  const url =
    typeof rawUrl === "string" && rawUrl.trim().length > 0
      ? rawUrl.trim()
      : null;
  const label = (
    <>
      <img src={icon.url} alt={source} />
      <span style={{ marginLeft: 8 }} />
      {url ? (
        <a href={url} target="_blank" rel="noopener noreferrer">
          {value}
        </a>
      ) : (
        value
      )}
    </>
  );
  return label;
}

export function buildLeaseColumns(
  renderSourceLink: (params: any) => ReactNode,
  onManuallyValidatedChange?: CompCheckboxChangeHandler,
  resolveValidationUserName?: CompValidationUserNameResolver,
): ColDef[] {
  return [
    validatedColumn(onManuallyValidatedChange, resolveValidationUserName),
    {
      field: "source",
      headerName: "Source",
      cellRenderer: renderSourceLink,
      width: cellW(150),
    },
    { field: "deal_name", headerName: "Deal name", width: cellW(150) },
    {
      field: "date",
      headerName: "Date",
      valueFormatter: shortDateFormatter,
      width: cellW(100),
    },
    { field: "tenant_name", headerName: "Tenant", width: cellW(150) },
    { field: "asset_name", headerName: "Asset", width: cellW(150) },
    { field: "city", headerName: "City", width: cellW(150) },
    { field: "lease_term", headerName: "Lease Term", width: cellW(100) },
    {
      field: "total_rent",
      headerName: "Total Rent",
      valueFormatter: numberFormatter,
      width: cellW(100),
    },
    { field: "currency", headerName: "Currency", width: cellW(100) },
    {
      field: "area",
      headerName: "Area",
      valueFormatter: numberFormatter,
      width: cellW(100),
    },
    { field: "area_type", headerName: "Area Type", width: cellW(100) },
    {
      field: "rent_per_area",
      headerName: "Rent per area",
      valueFormatter: numberFormatter,
      width: cellW(100),
    },
    {
      field: "age_refurb_date",
      headerName: "Built / refurbished",
      width: cellW(130),
    },
  ];
}

export function buildLoanColumns(
  renderSourceLink: (params: any) => ReactNode,
  onManuallyValidatedChange?: CompCheckboxChangeHandler,
  resolveValidationUserName?: CompValidationUserNameResolver,
): ColDef[] {
  return [
    validatedColumn(onManuallyValidatedChange, resolveValidationUserName),
    {
      field: "source",
      headerName: "Source",
      cellRenderer: renderSourceLink,
      width: cellW(150),
    },
    {
      field: "date",
      headerName: "Date",
      valueFormatter: shortDateFormatter,
      width: cellW(150),
    },
    { field: "lender", headerName: "Lender", width: cellW(150) },
    { field: "borrower", headerName: "Borrower", width: cellW(150) },
    { field: "deal_name", headerName: "Deal Name", width: cellW(150) },
    { field: "loan_type", headerName: "Loan Type", width: cellW(150) },
    { field: "sector_name", headerName: "Sector", width: cellW(150) },
    {
      field: "location",
      headerName: "Location",
      valueGetter: loanLocationWithCountry,
      width: cellW(180),
    },
    {
      field: "loan_amount",
      headerName: "Loan Amount",
      valueFormatter: numberFormatter,
      width: cellW(150),
    },
    {
      field: "margin",
      headerName: "Margin",
      valueFormatter: percentageFormatter,
      width: cellW(150),
    },
    {
      field: "ltc_ltv",
      headerName: "LTV",
      valueFormatter: percentageFormatter,
      width: cellW(150),
    },
    { field: "comment", headerName: "Comment", width: 320, minWidth: 280 },
  ];
}

export function buildSalesColumns(
  renderSourceLink: (params: any) => ReactNode,
  onManuallyValidatedChange?: CompCheckboxChangeHandler,
  resolveValidationUserName?: CompValidationUserNameResolver,
): ColDef[] {
  return [
    validatedColumn(onManuallyValidatedChange, resolveValidationUserName),
    {
      field: "source",
      headerName: "Source",
      cellRenderer: renderSourceLink,
      width: cellW(150),
    },
    {
      field: "date",
      headerName: "Date",
      valueFormatter: shortDateFormatter,
      width: cellW(150),
    },
    { field: "location", headerName: "Location", width: cellW(150) },
    { field: "vendor", headerName: "Vendor", width: cellW(150) },
    { field: "buyer", headerName: "Buyer", width: cellW(150) },
    {
      field: "sales_price",
      headerName: "Price",
      valueFormatter: numberFormatter,
      width: cellW(150),
    },
    { field: "currency", headerName: "Currency", width: cellW(150) },
    {
      field: "yield_pct",
      headerName: "Yield",
      valueFormatter: percentageFormatter,
      width: cellW(150),
    },
    {
      field: "rent",
      headerName: "Rent",
      valueFormatter: numberFormatter,
      width: cellW(150),
    },
    { field: "no_of_keys_or_rooms", headerName: "Rooms", width: cellW(150) },
    {
      field: "price_per_area",
      headerName: "Price per area",
      valueFormatter: numberFormatter,
      width: cellW(150),
    },
    {
      field: "price_per_key_room",
      headerName: "Price per key / room",
      valueFormatter: numberFormatter,
      width: cellW(150),
    },
    {
      field: "age_refurb_date",
      headerName: "Built / refurbished",
      width: cellW(130),
    },
  ];
}
